import { useEffect, useRef, useState } from 'preact/hooks';
import { services } from '../app';
import { alignOfferDue, alignOfferShown, alignTargets, aligning, hereAndNow, linedUp, markAlignOffered, nextTarget, startAligning, stopAligning, tryAgain, type AlignTarget } from '../live/align';
import { calendarFilmOpen, introOpen, panel, pointingPaused, previewFrom, reliving, selection, storyOpen, timeLive, viewMode, whatToShow } from '../state';
import { whereInSky } from './format';
import { Icon } from './icons';

// Align with the sky (src/live/align.ts): the card that walks the person through lining up, low over the sky so the
// circle in the middle stays in view (the bar along the bottom steps aside meanwhile), and the offer made once a night
// at a quiet moment. Look of "New to Ahillah?".

const lower = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
/** "the Moon, halfway up in the south-east". */
const findIt = (t: AlignTarget) => `${t.label}, ${lower(whereInSky(t.alt, t.az))}`;

/** Where focus goes once the card has gone: the Align chip, back in its place. */
const focusChip = () => setTimeout(() => document.querySelector<HTMLElement>('.align-chip')?.focus({ preventScroll: true }), 60);

/** While lining up, in pointing mode: what to find, and the taps that finish it. */
export function AlignCard() {
  const a = aligning.value;
  const ref = useRef<HTMLDivElement>(null);
  const on = !!a && viewMode.value === 'ar';
  // Each second, to notice the phone losing north (an iPhone back from the background) and finding it again.
  const [, tick] = useState(0);
  useEffect(() => {
    if (!on) return;
    const id = setInterval(() => tick((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, [on]);
  useEffect(() => {
    if (!on) return;
    ref.current?.focus({ preventScroll: true });
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      stopAligning();
      focusChip();
    };
    addEventListener('keydown', key);
    return () => removeEventListener('keydown', key);
  }, [on]);
  // It ends when pointing ends (a drag only pauses it), when a sheet, the tour or Sky Hunt opens, or when another time
  // or place comes on show.
  const ended = !!a && ((viewMode.value !== 'ar' && !pointingPaused.value) || !!panel.value || !!selection.value || !timeLive.value || !!previewFrom.value || reliving.value);
  useEffect(() => {
    if (ended) stopAligning();
  }, [ended]);
  const s = services.value;
  if (!on || !s || ended) return null;
  const t = a.targets[a.i];
  // Without north the phone's bearing means nothing yet: the card says how to help, as the Align chip does.
  const north = s.engine.orientation.northKnown;
  const close = (
    <button class="align-close" aria-label="Cancel" onClick={() => { stopAligning(); focusChip(); }}>
      <Icon name="close" size={14} stroke={2.4} />
    </button>
  );
  if (a.check !== undefined) {
    return (
      <div class="guide-offer align-card" role="dialog" aria-labelledby="align-title" aria-describedby="align-text" tabIndex={-1} ref={ref}>
        {close}
        <h2 id="align-title">Is that {t.label}?</h2>
        <p id="align-text">That turns the sky by {Math.round(Math.abs(a.check))}°, more than a compass is usually out. Is the circle really on {t.label}?</p>
        <div class="guide-offer-actions">
          <button class="btn-ghost" onClick={tryAgain}>Try again</button>
          <button class="btn" onClick={() => linedUp(s, true) === 'done' && focusChip()}>Yes, align</button>
        </div>
      </div>
    );
  }
  return (
    <div class="guide-offer align-card" role="dialog" aria-labelledby="align-title" aria-describedby="align-text" tabIndex={-1} ref={ref}>
      {close}
      <h2 id="align-title">Align with the sky</h2>
      <p id="align-text">{north ? `Find ${findIt(t)}. Hold your phone up so the circle covers it, then tap Lined up.` : 'Finding north. Point the phone at the horizon for a moment.'}</p>
      <div class="guide-offer-actions">
        {a.targets.length > 1 && <button class="btn-ghost" onClick={nextTarget}>Another one</button>}
        <button class="btn" disabled={!north} onClick={() => linedUp(s) === 'done' && focusChip()}>Lined up</button>
      </div>
    </div>
  );
}

/** Once a night, after dark, at a quiet moment in pointing mode, with the sky here and now. */
export function AlignOffer({ blocked }: { blocked: boolean }) {
  const [n, tick] = useState(0);
  const [shown, setShown] = useState<AlignTarget | null>(null);
  const [unsure, setUnsure] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const before = useRef<HTMLElement | null>(null);
  /** Since when it has been quiet (null while it is not). */
  const quietSince = useRef<number | null>(null);
  useEffect(() => {
    const id = setInterval(() => tick((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const s = services.value;
  // Quiet: Ahillah on screen and following the phone, here and now, with nothing else asking for attention.
  const quiet = !blocked && (typeof document === 'undefined' || document.visibilityState === 'visible') && viewMode.value === 'ar' && !panel.value && !selection.value
    && !aligning.value && !pointingPaused.value && !introOpen.value && !calendarFilmOpen.value && !storyOpen.value && !whatToShow.value && hereAndNow()
    && !!s?.engine.orientation.northKnown;
  useEffect(() => {
    // Dealt with as soon as anything else happens: lining up another way, pointing ended, a sheet opened. Made once a
    // night, it does not come back.
    if (!quiet) {
      quietSince.current = null;
      if (shown) setShown(null);
      return;
    }
    quietSince.current ??= Date.now();
    // After a few moments of looking round, and checked again each second until it is due (darkness falling, say).
    if (shown || !s || Date.now() - quietSince.current < 8000 || !alignOfferDue(s)) return;
    const t = alignTargets(s)[0];
    if (!t) return;
    markAlignOffered();
    setUnsure(s.engine.orientation.headingAccuracy > 20);
    setShown(t);
  }, [n, quiet]);
  const visible = !!shown && quiet && !!s;
  const close = () => {
    setShown(null);
    const to = before.current;
    before.current = null;
    setTimeout(() => (to && document.contains(to) ? to : null)?.focus({ preventScroll: true }), 0);
  };
  useEffect(() => {
    alignOfferShown.value = visible;
    if (!visible) return;
    before.current = document.activeElement as HTMLElement | null;
    ref.current?.focus({ preventScroll: true });
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      close();
    };
    addEventListener('keydown', key);
    return () => removeEventListener('keydown', key);
  }, [visible]);
  if (!visible) return null;
  return (
    <div class="guide-offer align-offer" role="dialog" aria-labelledby="align-offer-title" aria-describedby="align-offer-text" tabIndex={-1} ref={ref}>
      <h2 id="align-offer-title"><span class="guide-offer-icon" aria-hidden="true"><Icon name="target" size={18} stroke={1.9} /></span>{unsure ? 'The compass seems unsure' : 'Does the sky match?'}</h2>
      <p id="align-offer-text">Line it up with {shown.label} in one tap, so what you see on the screen sits on the real sky.</p>
      <div class="guide-offer-actions">
        <button class="btn-ghost" onClick={close}>Not now</button>
        <button class="btn" onClick={() => { setShown(null); startAligning(s); }}>Align</button>
      </div>
    </div>
  );
}

/** For the Align chip and Settings: start lining up. */
export function beginAligning() {
  const s = services.peek();
  if (s) startAligning(s);
}
